/**
 * What a class has covered — as a table, like every other admin list.
 *
 * The student app renders this as a compact card, because a student is
 * reporting on one chapter at a time. An administrator is auditing thirty
 * rows and wants the same tools as everywhere else: sortable columns, a
 * filter bar, and one row per thing.
 *
 * Coverage is a set of REPORTS, not a flag: corroboration count is the trust
 * signal, disagreement is representable rather than last-writer-wins, and a
 * retraction is a report rather than a deletion. The columns exist to make
 * that legible — "who says" and "who disputes" are separate numbers.
 */
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button, DataTable, SearchFilterBar,
  type ColumnDef, type FilterDef, type SortState,
} from '@shared-ui/components';
import { useSubjectLabel } from '@shared-ui/reference-data';
import { curriculumApi } from '../../api/curriculumApi';
import { classSharedApi, schoolsApi, type SchoolClass } from '../../api/schoolsApi';
import { PUBLIC_TENANT_ID } from '../../api/tenantsApi';

interface Row {
  chapterId: string;
  number: string;
  name: string;
  /** The people, not just how many — a count of 3 raises "which 3?", and
   *  answering it is the difference between a number and evidence. */
  coveredBy: string[];
  disputedBy: string[];
  covered: boolean;
  lastReportedAt?: string;
}

/** Reported / disputed / silent — what an auditor actually filters by. */
function stateOf(r: Row): 'covered' | 'disputed' | 'not-covered' | 'unreported' {
  if (r.coveredBy.length === 0 && r.disputedBy.length === 0) return 'unreported';
  if (r.coveredBy.length > 0 && r.disputedBy.length > 0) return 'disputed';
  return r.covered ? 'covered' : 'not-covered';
}

const STATE_LABELS: Record<ReturnType<typeof stateOf>, string> = {
  covered: 'Covered',
  disputed: 'Disputed',
  'not-covered': 'Not covered',
  unreported: 'Nobody has said',
};

export function CoverageTab({ cls, subjects }: { cls: SchoolClass; subjects: string[] }) {
  const qc = useQueryClient();
  /**
   * Who is entitled to say a chapter is done.
   *
   * On a PUBLIC record nobody is, so coverage is a set of reports: the
   * corroboration count is the trust signal, disagreement is representable, and
   * the columns exist to make that legible. In a tenant the teacher of the room
   * says it and that is the answer — there is nothing to corroborate and nobody
   * to disagree with, so counting reporters would dress one authoritative fact
   * up as a vote.
   */
  const tenantOwned = !!cls.tenantId && cls.tenantId !== PUBLIC_TENANT_ID;
  // Subject lives in the filter values with everything else, so it sits on the
  // one filter row rather than floating in a toolbar of its own.
  const [filters, setFilters] = useState<Record<string, string>>({
    subject: subjects[0] ?? 'maths',
  });
  const subject = filters.subject || subjects[0] || 'maths';
  const [sort, setSort] = useState<SortState | undefined>({ sortBy: 'number', sortOrder: 'asc' });
  // A subject's internal name is a key, not a label — resolve it through the
  // catalogue everywhere a person reads it.
  const subjectLabel = useSubjectLabel();

  /** Reporters come back as user ids. The endpoint resolves them — including
   *  people who are not members, which is why the roster alone was not enough
   *  and a teacher's report read as a truncated uuid. The roster is the
   *  fallback, and a short id the last resort. */
  const { data: roster } = useQuery({
    queryKey: ['class-members', cls.id],
    queryFn: () => schoolsApi.listClassMembers(cls.id),
  });

  const { data: curricula } = useQuery({
    queryKey: ['curricula'],
    queryFn: () => curriculumApi.listCurricula(),
    enabled: !!cls.board,
  });

  /**
   * The class's own syllabus: board + grade + subject.
   *
   * Several curricula match that triple — a TEACHER's private copy sits
   * alongside the platform's — and taking whichever came back first found a
   * two-chapter test syllabus instead of the real book. A class follows the
   * published one, so teacher-owned and inactive rows are excluded outright.
   */
  const curriculum = useMemo(() => {
    if (!cls.board) return undefined;
    return (curricula?.curricula ?? []).find(c =>
      c.board === cls.board
      && String(c.grade ?? '') === String(cls.grade)
      && c.subject === subject
      && c.ownerType !== 'teacher'
      && c.isActive !== false);
  }, [curricula, cls.board, cls.grade, subject]);

  const { data: placements } = useQuery({
    queryKey: ['curriculum-placements', curriculum?.id],
    queryFn: () => curriculumApi.getPlacements(curriculum!.id),
    enabled: !!curriculum?.id,
  });

  const { data: coverage, isLoading } = useQuery({
    queryKey: ['class-coverage', cls.id, subject],
    queryFn: () => classSharedApi.getCoverage(cls.id, subject),
    enabled: !!curriculum?.id,
  });

  const nameOf = useMemo(() => {
    const fromServer = coverage?.names ?? {};
    const fromRoster = new Map((roster?.members ?? []).map(m => [m.userId, m.name]));
    return (userId: string) =>
      fromServer[userId] || fromRoster.get(userId) || `${userId.slice(0, 8)}…`;
  }, [coverage, roster]);

  const report = useMutation({
    mutationFn: ({ chapterId, covered }: { chapterId: string; covered: boolean }) =>
      classSharedApi.reportCoverage(cls.id, {
        subject, chapterId, curriculumId: curriculum!.id, covered,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['class-coverage', cls.id, subject] }),
  });

  const rows = useMemo<Row[]>(() => {
    const byChapter = new Map((coverage?.chapters ?? []).map(c => [c.chapterId, c]));
    const all = (placements?.placements ?? [])
      .filter(p => p.bookLevel === 'chapter')
      .map<Row>(p => {
        const c = byChapter.get(p.id);
        return {
          chapterId: p.id,
          number: p.number,
          name: p.displayName,
          coveredBy: c?.coveredBy ?? [],
          disputedBy: c?.disputedBy ?? [],
          covered: c?.covered ?? false,
          lastReportedAt: c?.lastReportedAt,
        };
      });

    const term = (filters.search ?? '').trim().toLowerCase();
    return all.filter(r => {
      // In a tenant the only two states are the two a teacher can set.
      if (filters.state) {
        const matches = tenantOwned
          ? (filters.state === 'covered') === r.covered
          : stateOf(r) === filters.state;
        if (!matches) return false;
      }
      if (!term) return true;
      return `${r.number} ${r.name}`.toLowerCase().includes(term);
    });
  }, [placements, coverage, filters, tenantOwned]);

  const filterDefs: FilterDef[] = [
    {
      // `required`: coverage is always against ONE subject, so there is no
      // "all subjects" reading of this table to offer.
      key: 'subject',
      placeholder: 'Subject',
      required: true,
      options: subjects.map(s => ({ value: s, label: subjectLabel(s) })),
    },
    {
      key: 'state',
      placeholder: 'All states',
      options: (tenantOwned
        ? (['covered', 'not-covered'] as const)
        : (Object.keys(STATE_LABELS) as Array<keyof typeof STATE_LABELS>)
      ).map(v => ({ value: v, label: STATE_LABELS[v] })),
    },
  ];

  const columns: ColumnDef<Row>[] = [
    {
      key: 'number',
      header: '#',
      sortable: true,
      // Numeric where it can be, so 10 sorts after 9 rather than after 1.
      sortValue: r => Number(r.number) || r.number,
      render: r => r.number,
    },
    { key: 'name', header: 'Chapter', sortable: true, sortValue: r => r.name.toLowerCase(), render: r => r.name },
    {
      key: 'state',
      header: 'State',
      sortable: true,
      sortValue: r => (tenantOwned ? String(!r.covered) : stateOf(r)),
      render: r => (tenantOwned
        ? STATE_LABELS[r.covered ? 'covered' : 'not-covered']
        : STATE_LABELS[stateOf(r)]),
    },
    // Two numbers, not one verdict: the count is the trust signal, and a tie is
    // deliberately not covered. Only where there is a crowd to count.
    // Sort by how many said it; render who. `String(ids)` would have printed
    // the raw user ids, which is what WhoSaid and nameOf exist to avoid.
    ...(tenantOwned ? [] : [
      {
        key: 'coveredBy', header: 'Say covered', sortable: true,
        sortValue: (r: Row) => r.coveredBy.length,
        render: (r: Row) => <WhoSaid userIds={r.coveredBy} nameOf={nameOf} />,
      } satisfies ColumnDef<Row>,
      {
        key: 'disputedBy', header: 'Say not', sortable: true,
        sortValue: (r: Row) => r.disputedBy.length,
        render: (r: Row) => <WhoSaid userIds={r.disputedBy} nameOf={nameOf} />,
      } satisfies ColumnDef<Row>,
    ]),
    {
      key: 'actions',
      header: '',
      render: r => (
        <Button
          variant="secondary"
          size="sm"
          disabled={report.isPending}
          title={r.covered
            ? 'Record that this has NOT been covered'
            : 'Record that this has been covered'}
          onClick={() => report.mutate({ chapterId: r.chapterId, covered: !r.covered })}
        >
          {r.covered ? 'Mark not covered' : 'Mark covered'}
        </Button>
      ),
    },
  ];

  if (!cls.board) {
    return (
      <section className="school-section">
        <p className="school-hint">
          This class has no board set, so it has no shared syllabus to record coverage against.
          Set the board on the General tab first.
        </p>
      </section>
    );
  }

  return (
    <section className="school-section">
      {/* Outside the branch below: pick a subject with no syllabus and the bar
          is how you pick your way back out. */}
      <SearchFilterBar
        filters={filterDefs}
        values={filters}
        onChange={setFilters}
        searchPlaceholder="Search chapter…"
        hideFetch
      />

      {!curriculum ? (
        <p className="school-hint">
          No {cls.board} grade-{cls.grade} {subjectLabel(subject)} curriculum published to
          record against.
        </p>
      ) : (
        <>
          <DataTable<Row>
            columns={columns}
            data={rows}
            rowKey={r => r.chapterId}
            loading={isLoading}
            bordered
            sort={sort}
            onSortChange={setSort}
            emptyMessage="No chapters match those filters."
          />
          {!tenantOwned && (
            <p className="school-hint">
              Anyone in the class can report, and a count of several is what makes it
              trustworthy — a tie is deliberately not counted as covered. Marking here never
              erases what a student said.
            </p>
          )}
          {report.isError && <p className="school-error">{(report.error as Error).message}</p>}
        </>
      )}
    </section>
  );
}

/**
 * Who said it — the names, in the cell.
 *
 * A count behind a hover made the common question ("who marked this?") cost an
 * interaction, and told a touch screen nothing at all. The names ARE the value
 * of this column, so they are what it renders; the count was only ever a
 * stand-in for them.
 */
function WhoSaid({ userIds, nameOf }: {
  userIds: string[];
  nameOf: (userId: string) => string;
}) {
  if (userIds.length === 0) return <span className="cov-count-zero">—</span>;
  return (
    <span className="cov-who-inline">
      {userIds.map(id => <span key={id} className="cov-who-name">{nameOf(id)}</span>)}
    </span>
  );
}
